import { defineAsyncComponent } from 'vue';

export default {
    slug: 'tasks',
    ability: 'tasks',
    components(app) {
        app.component(
            'app-tasks',
            defineAsyncComponent(() => import('./components/tasks/Tasks.vue'))
        );
    },
    routes: [
        {
            path: 'tasks',
            name: 'task-list',
            component: () => import('@modules/Tasks/Resources/js/pages/List.vue'),
            // What the list asks for as it opens, in one request (utils/listBootstrap.js).
            // No object: a task's layout is read per variant, which the bootstrap does not carry.
            meta: { listBootstrap: { list: 'tasks' } },
            children: [
                {
                    // Creating opens the panel a task is read in, sliding over
                    // the list exactly as it slides over a project's board —
                    // same component, same width, same handle.
                    //
                    // A *named* view, because the list already has a
                    // `<router-view>` for the record it shows beside the rows:
                    // declared as the default one, the panel would be drawn
                    // twice, once over the list and once inside it.
                    //
                    // Declared before the numeric route for the reader's sake;
                    // a static segment wins over a parameter whatever the order.
                    path: 'new',
                    name: 'task-new',
                    components: {
                        slideover: () => import('@modules/Tasks/Resources/js/components/tasks/TaskPanel.vue')
                    },
                    props: { slideover: { create: true, owner: null } }
                },
                {
                    path: ':id(\\d+)',
                    name: 'task-preview',
                    component: () => import('@modules/Tasks/Resources/js/pages/Preview.vue'),
                    redirect: { name: 'task-overview' },
                    props: true,
                    children: [
                        {
                            path: '/organization/:org/tasks/:id(\\d+)',
                            name: 'task-overview',
                            component: () => import('@modules/Tasks/Resources/js/pages/preview/Overview.vue'),
                            props: true
                        },
                        {
                            path: 'comments',
                            name: 'task-comments',
                            component: () => import('@/components/base/tabs/TabComments.vue'),
                            props: true
                        },
                        {
                            path: 'timesheets',
                            name: 'task-timesheets',
                            component: () => import('@/components/base/tabs/TabTimesheets.vue'),
                            props: true
                        },
                        {
                            path: 'activities',
                            name: 'task-activities',
                            component: () => import('@/components/base/tabs/TabActivities.vue'),
                            props: true
                        },
                        {
                            path: 'documents/:docPath(.*)*',
                            name: 'task-documents',
                            component: () => import('@/components/base/tabs/TabDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'document-requests',
                            name: 'task-document-requests',
                            component: () => import('@/components/base/tabs/TabDocumentRequests.vue'),
                            props: true
                        },
                        {
                            path: 'logs',
                            name: 'task-logs',
                            component: () => import('@/components/base/tabs/TabLogs.vue'),
                            props: true
                        }
                    ]
                }
            ]
        }
    ],
    generalTabs: () => [
        {
            position: 3,
            title: i18n.t('Tasks'),
            icon: 'calendar-check',
            route: 'settings-general-tasks',
            ability: 'settings.tasks'
        },
        // Its own tab rather than a fourth box under « Tâches »: that screen
        // holds the application's behaviour, this one holds how work is
        // structured. The same split Work Orders makes between its settings
        // and its types.
        {
            position: 4,
            title: i18n.t('Task types'),
            icon: 'shape-outline',
            route: 'settings-general-task-types',
            ability: 'settings.tasks'
        }
    ],
    generalRoutes: [
        {
            path: 'tasks',
            name: 'settings-general-tasks',
            component: () => import('@modules/Tasks/Resources/js/pages/settings/TaskManagementSettings.vue'),
            props: true
        },
        {
            path: 'task-types',
            name: 'settings-general-task-types',
            component: () => import('@modules/Tasks/Resources/js/pages/settings/TaskWorkflowSettings.vue'),
            props: true
        }
    ],
    // At the top level rather than inside a domain: a task is a billable unit of
    // work on a site and a follow-up on a deal, and the calendar carries both
    // kinds of appointment. Filing either under CRM or under Operations would
    // pick a side the work does not have — and would hide it from the half of
    // the application that was not chosen. The navigation already keeps this
    // place for what belongs to everyone; the dashboard sits here too.
    nav: () => ({
        section: 'global',
        items: [
            {
                position: 2,
                title: i18n.t('Tasks'),
                icon: 'calendar-check',
                to: { name: 'task-list' }
            }
        ]
    }),
    widgets: () => [
        {
            id: 'my-tasks',
            label: i18n.t('My tasks'),
            icon: 'calendar-check',
            component: () => import('@modules/Tasks/Resources/js/pages/dashboards/widgets/MyTasks.vue'),
            size: 'is-6',
            module: 'tasks'
        }
    ]
};
